import { useEffect, useRef } from 'react';

import { useUser } from '@clerk/react';
import { type InboxEntry, WebSocketEntityType } from '@this+that/models';

import { isOpen } from '../../../hooks/SuggestionTabAlerts/entryInsertPredicates';
import { isQualifyingApprovalInsert } from '../../../hooks/SuggestionTabAlerts/isQualifyingApprovalInsert';
import { isQualifyingSuggestionInsert } from '../../../hooks/SuggestionTabAlerts/isQualifyingSuggestionInsert';
import { isSuggestionSoundEnabled } from '../../../hooks/SuggestionTabAlerts/isSuggestionSoundEnabled';
import { isUserLookingAtThisTab } from '../../../hooks/SuggestionTabAlerts/isUserLookingAtThisTab';
import {
  publishTabAlertState,
  recordTabAlert,
  removeTabAlert,
} from '../../../hooks/SuggestionTabAlerts/recordTabAlert';
import {
  buildApprovalAlertKey,
  markAllSeen,
  parseSuggestionTabAlertsState,
} from '../../../hooks/SuggestionTabAlerts/suggestionTabAlertsState';
import {
  buildSuggestionTabAlertsKey,
  readSuggestionTabAlertsState,
  updateSuggestionTabAlertsState,
} from '../../../hooks/SuggestionTabAlerts/suggestionTabAlertsStorage';
import { useFaviconBadge } from '../../../hooks/SuggestionTabAlerts/useFaviconBadge';
import { useUnlockAudioOnInteraction } from '../../../hooks/SuggestionTabAlerts/useUnlockAudioOnInteraction';
import { DOM_EVENTS } from '../../../hooks/usePushNotifications';
import { useSuggestionTabAlertsStore } from '../../../store/SuggestionTabAlerts/useSuggestionTabAlertsStore';
import { WINDOW_FOCUS_EVENT } from '../../../utils/constants/suggestionTabAlerts';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const STORAGE_EVENT = 'storage';

const resolveAlertKey = (
  item: InboxEntry,
  viewer: { userId: string; defaultTaskGroupId: string | null },
): string | null => {
  if (isQualifyingSuggestionInsert(item, viewer)) return item.id;
  if (isQualifyingApprovalInsert(item, viewer))
    return buildApprovalAlertKey(item.id);
  return null;
};

interface Viewer {
  defaultTaskGroupId: string | null;
  soundEnabled: boolean;
}

const markSeenAndSync = async (userId: string) => {
  const state = await updateSuggestionTabAlertsState(userId, (current) => ({
    next: markAllSeen(current),
  }));
  publishTabAlertState(state);
};

const syncOnMount = (userId: string) => {
  if (isUserLookingAtThisTab()) {
    void markSeenAndSync(userId);
    return;
  }
  publishTabAlertState(
    readSuggestionTabAlertsState(buildSuggestionTabAlertsKey(userId)),
  );
};

export function useSuggestionTabAlertsSync() {
  const { subscribe } = useWebSocket();
  const { user } = useUser();
  const userId = user?.id;
  const unseenCount = useSuggestionTabAlertsStore((s) => s.unseenCount);

  const viewerRef = useRef<Viewer>({
    defaultTaskGroupId: null,
    soundEnabled: false,
  });
  viewerRef.current = {
    defaultTaskGroupId:
      (user?.unsafeMetadata?.defaultTaskGroupId as string | undefined) ?? null,
    soundEnabled: isSuggestionSoundEnabled(user?.unsafeMetadata),
  };

  useFaviconBadge(unseenCount > 0);
  useUnlockAudioOnInteraction();

  useEffect(() => {
    if (!userId) {
      useSuggestionTabAlertsStore
        .getState()
        .setUnseen({ count: 0, hasAttention: false });
      return;
    }

    syncOnMount(userId);

    const unsubscribe = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.INSERT,
      ({ item }) => {
        const { defaultTaskGroupId, soundEnabled } = viewerRef.current;
        if (!item) return;
        const key = resolveAlertKey(item, { userId, defaultTaskGroupId });
        if (key) {
          void recordTabAlert({
            userId,
            key,
            createdAt: item.createdAt ?? Date.now(),
            soundEnabled,
          });
        }
      },
    );

    const dropApprovalAlert = (item: InboxEntry | undefined) => {
      if (item) void removeTabAlert(userId, buildApprovalAlertKey(item.id));
    };
    const unsubscribeModify = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.MODIFY,
      ({ item }) => {
        if (item && !isOpen(item)) dropApprovalAlert(item);
      },
    );
    const unsubscribeRemove = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.REMOVE,
      ({ item }) => dropApprovalAlert(item),
    );

    const handleVisibilityChange = () => {
      if (isUserLookingAtThisTab()) void markSeenAndSync(userId);
    };
    const handleWindowFocus = () => {
      void markSeenAndSync(userId);
    };

    const storageKey = buildSuggestionTabAlertsKey(userId);
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      publishTabAlertState(parseSuggestionTabAlertsState(event.newValue));
    };

    document.addEventListener(
      DOM_EVENTS.VISIBILITY_CHANGE,
      handleVisibilityChange,
    );
    window.addEventListener(WINDOW_FOCUS_EVENT, handleWindowFocus);
    window.addEventListener(STORAGE_EVENT, handleStorage);

    return () => {
      unsubscribe();
      unsubscribeModify();
      unsubscribeRemove();
      document.removeEventListener(
        DOM_EVENTS.VISIBILITY_CHANGE,
        handleVisibilityChange,
      );
      window.removeEventListener(WINDOW_FOCUS_EVENT, handleWindowFocus);
      window.removeEventListener(STORAGE_EVENT, handleStorage);
    };
  }, [userId, subscribe]);
}
